import { useEffect, useMemo, useState } from 'react';
import { migrateSelectionColumnVisibility } from '../../utils/productSelectionsLegacy';

const useProductSelectionsTableState = ({
  userEmail,
  initialFilterModel,
  initialPageSize,
  storageKeySuffix = '',
}) => {
  const columnStorageKey = useMemo(() => {
    const email = userEmail ? String(userEmail).toLowerCase() : 'guest';
    const suffix = storageKeySuffix ? `:${storageKeySuffix}` : '';
    return `productSelectionsTableColumns:${email}${suffix}`;
  }, [storageKeySuffix, userEmail]);

  const tableStateStorageKey = useMemo(() => {
    const email = userEmail ? String(userEmail).toLowerCase() : 'guest';
    const suffix = storageKeySuffix ? `:${storageKeySuffix}` : '';
    return `productSelectionsTableState:${email}${suffix}`;
  }, [storageKeySuffix, userEmail]);

  const loadColumnState = useMemo(() => {
    if (typeof window === 'undefined') return { visibility: {}, ordering: [], pinning: { left: [], right: [] } };
    try {
      const stored = window.localStorage.getItem(columnStorageKey);
      if (!stored) return { visibility: {}, ordering: [], pinning: { left: [], right: [] } };
      const parsed = JSON.parse(stored);
      // Handle legacy format (just visibility)
      if (!parsed.visibility && !parsed.ordering && !parsed.pinning) {
        return {
          visibility: migrateSelectionColumnVisibility(parsed),
          ordering: [],
          pinning: { left: [], right: [] },
        };
      }
      return {
        visibility: migrateSelectionColumnVisibility(parsed.visibility || {}),
        ordering: Array.isArray(parsed.ordering) ? parsed.ordering : [],
        pinning:
          parsed.pinning && typeof parsed.pinning === 'object'
            ? {
                left: Array.isArray(parsed.pinning.left) ? parsed.pinning.left : [],
                right: Array.isArray(parsed.pinning.right) ? parsed.pinning.right : [],
              }
            : { left: [], right: [] },
      };
    } catch (_err) {
      return { visibility: {}, ordering: [], pinning: { left: [], right: [] } };
    }
  }, [columnStorageKey]);

  const loadTableState = useMemo(() => {
    if (typeof window === 'undefined') return { globalFilter: '', columnFilters: [], sorting: [] };
    try {
      const stored = window.localStorage.getItem(tableStateStorageKey);
      if (!stored) return { globalFilter: '', columnFilters: [], sorting: [] };
      const parsed = JSON.parse(stored);
      return {
        globalFilter: typeof parsed?.globalFilter === 'string' ? parsed.globalFilter : '',
        columnFilters: Array.isArray(parsed?.columnFilters)
          ? parsed.columnFilters.filter((item) => item && item.id && item.value != null)
          : [],
        sorting: Array.isArray(parsed?.sorting)
          ? parsed.sorting.filter((item) => item && item.id).map((item) => ({ id: item.id, desc: Boolean(item.desc) }))
          : [],
      };
    } catch (_err) {
      return { globalFilter: '', columnFilters: [], sorting: [] };
    }
  }, [tableStateStorageKey]);

  const [columnVisibility, setColumnVisibility] = useState(loadColumnState.visibility);
  const [columnOrder, setColumnOrder] = useState(loadColumnState.ordering);
  const [columnPinning, setColumnPinning] = useState(loadColumnState.pinning);
  const [globalFilter, setGlobalFilter] = useState(loadTableState.globalFilter);
  const [columnFilters, setColumnFilters] = useState(loadTableState.columnFilters);
  const [sorting, setSorting] = useState(loadTableState.sorting);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stateToStore = {
        visibility: columnVisibility,
        ordering: columnOrder,
        pinning: columnPinning,
      };
      window.localStorage.setItem(columnStorageKey, JSON.stringify(stateToStore));
    } catch (_err) {
      // noop
    }
  }, [columnStorageKey, columnVisibility, columnOrder, columnPinning]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stateToStore = {
        globalFilter,
        columnFilters,
        sorting,
      };
      window.localStorage.setItem(tableStateStorageKey, JSON.stringify(stateToStore));
    } catch (_err) {
      // noop
    }
  }, [tableStateStorageKey, globalFilter, columnFilters, sorting]);

  useEffect(() => {
    if (!initialFilterModel?.items) return;
    const items = (initialFilterModel.items || [])
      .filter((i) => i?.field && i?.value != null && String(i.value).trim() !== '')
      .slice(0, 6)
      .map((i) => ({ id: i.field, value: String(i.value) }));
    if (items.length) setColumnFilters(items);
  }, [initialFilterModel]);

  const tableInitialState = {
    showGlobalFilter: false,
    showColumnFilters: false,
    density: 'compact',
    columnPinning:
      loadColumnState.pinning.left.length > 0 || loadColumnState.pinning.right.length > 0
        ? loadColumnState.pinning
        : { left: ['sendToStaging'] },
    columnOrder: loadColumnState.ordering.length > 0 ? loadColumnState.ordering : undefined,
    ...(initialPageSize ? { pagination: { pageIndex: 0, pageSize: initialPageSize } } : {}),
  };

  return {
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    setColumnOrder,
    columnPinning,
    setColumnPinning,
    globalFilter,
    setGlobalFilter,
    columnFilters,
    setColumnFilters,
    sorting,
    setSorting,
    tableInitialState,
  };
};

export default useProductSelectionsTableState;
